/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Suspense, useCallback, useEffect, useState} from 'react';
import {Slide, ToastContainer} from 'react-toastify';
import {injectStyle} from 'react-toastify/inject-style';

import ActiveData from 'coreComponents/ActiveData';
import CmsPreviewProvider from 'coreComponents/CmsPreviewProvider';
import {SuspenseSEO} from 'coreComponents/SEO';
import {
	DeferredFallbacksContext,
	IsServerFallback,
	useActiveData,
	useHandleActiveDataEvents,
	useLocaleChangeHandler,
	useLocalesMgr,
	useLocation,
	useUpdateLocale,
} from 'coreHooks';
import {getConfig} from 'coreUtils/utils';
import {watchOnlineStatus} from 'coreUtils/utils';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {EmarsysZones, SvgSprite} from 'brandComponents';
import {MinicartCountProvider} from 'brandComponents/MinicartCount';
import {ACTIVE_DATA_ENABLED} from 'brandConstants';
import useAnalytics from 'brandHooks/useAnalytics';

import OfflineBanner from '../../components/OfflineBanner';

import App from './Subcomponents/App';
import AppEffects from './Subcomponents/App/AppEffects';

//import styles
// eslint-disable-next-line no-restricted-imports
import 'brandSCSS/base.global.scss';
// Global, not a CSS module: Vite drops a `.module.scss` imported only for its side effects.
// eslint-disable-next-line no-restricted-imports
import 'brandSCSS/fonts.global.scss';

const Effects = (props) => {
	const {managerRepository} = useGeneralProvider();
	const [showToastify, setShowToastify] = useState(false);
	const handleActiveDataEvents = useHandleActiveDataEvents();
	const {cookieProcessed} = useUpdateLocale(props);

	useLocaleChangeHandler();

	useEffect(() => {
		managerRepository.getEventsMgr().subscribe(handleActiveDataEvents);
	}, []);

	useEffect(() => {
		setTimeout(() => {
			injectStyle(); // TODO: import css/scss from toastify or develop custom theme instead
			setShowToastify(true); // Toastify forces style recalculation, delaying it we avoid impact on performance scores.
		}, 5000);
	}, []);

	return (
		<>
			{showToastify && (
				<ToastContainer
					position="top-center"
					autoClose={5000}
					hideProgressBar={true}
					closeOnClick
					transition={Slide}
					closeButton={false}
					theme="colored"
					icon={false}
				/>
			)}
			<AppEffects {...props} isReady={cookieProcessed} />
		</>
	);
};

const AppRoot = (props) => {
	const appConfig = getConfig();
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const localesMgr = useLocalesMgr();
	const [isOnline, setIsOnline] = useState(true);
	const currentLocale = localesMgr.getCurrentLocale();
	const activeData = useActiveData();
	const currentSite = managerRepository.getSfccAPI()?.getSiteId();
	const trackPage = useCallback(() => {
		activeData.trackPage(currentSite, currentLocale.getCode(), currentLocale.getCurrencyCode());
	}, [currentSite, currentLocale.getCode(), currentLocale.getCurrencyCode(), activeData]);

	useEffect(() => {
		// Listen for online status changes.
		watchOnlineStatus((isOnline) => {
			setIsOnline(isOnline);
		});

		if (appConfig?.enableWdyr) {
			import('coreUtils/whyDidYouRender').then(({init}) => {
				init(appConfig.wdyrParameters);
			});
		}
	}, []);

	useEffect(() => {
		trackPage();
	}, [location.pathname]);

	useAnalytics();

	return (
		<CmsPreviewProvider>
			<IsServerFallback />
			<SvgSprite />

			{ACTIVE_DATA_ENABLED && <ActiveData trackPage={trackPage} />}
			{!isOnline && <OfflineBanner />}
			<Suspense fallback={null}>
				<SuspenseSEO />
			</Suspense>
			<DeferredFallbacksContext value={[]}>
				<MinicartCountProvider>
					{currentLocale.isValid() && <Effects {...props} />}
					<App {...props} />
				</MinicartCountProvider>
			</DeferredFallbacksContext>
			<EmarsysZones />
		</CmsPreviewProvider>
	);
};

export default AppRoot;
